import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../hooks/useLanguage';
import { cn } from '../utils/cn';
import { ProjectCard } from '../components/projects/ProjectCard';
import { projectApi } from '../services/api';

interface Project {
  id: number;
  slug: string;
  name_zh: string;
  name_vi: string;
  description_zh: string;
  description_vi: string;
  location_zh?: string;
  location_vi?: string;
  project_type?: string;
  end_date?: string;
  cover_image_url?: string;
  gallery_images?: string;
  is_featured: boolean;
  status: string;
}

const ProjectsPage: React.FC = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 项目点击导航（使用 useNavigate 替代 Link，解决路由跳转问题）
  const handleProjectClick = useCallback((projectId: number) => {
    console.log('[ProjectsPage] Navigating to project:', projectId);
    navigate(`/projects/${projectId}`);
  }, [navigate]);

  // 分类选项
  const categories = [
    { id: 'all', nameZh: '全部项目', nameVi: 'Tất cả dự án' },
    { id: 'electrical', nameZh: '电气工程', nameVi: 'Điện công nghiệp' },
    { id: 'hvac', nameZh: '暖通空调', nameVi: 'HVAC' },
    { id: 'ventilation', nameZh: '通风系统', nameVi: 'Thông gió' },
    { id: 'decoration', nameZh: '建筑装饰', nameVi: 'Trang trí kiến trúc' },
    { id: 'waterproofing', nameZh: '防水地坪', nameVi: 'Chống thấm sàn' },
    { id: 'renovation', nameZh: '改造工程', nameVi: 'Cải tạo' },
  ];

  useEffect(() => {
    const fetchProjects = async () => {
      setLoading(true);
      setError(null);
      try {
        const params: any = {};
        if (selectedCategory !== 'all') {
          params.project_type = selectedCategory;
        }
        const res = await projectApi.getProjects(params);
        setProjects(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error('获取项目失败:', err);
        setError(language === 'zh' ? '加载项目失败，请稍后重试' : 'Tải dự án thất bại, vui lòng thử lại');
        setProjects([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, [selectedCategory]);

  // 获取分类名称
  const getCategoryName = (categoryId: string) => {
    const category = categories.find(cat => cat.id === categoryId);
    return language === 'zh' ? category?.nameZh : category?.nameVi;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 页面标题 */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-12 md:py-16">
        <div className="container mx-auto px-4">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 md:mb-4">
            {language === 'zh' ? '项目案例' : 'Dự án tiêu biểu'}
          </h1>
          <p className="text-sm sm:text-base md:text-xl opacity-90 max-w-3xl">
            {language === 'zh'
              ? '展示我们在越南各地的成功项目案例，涵盖电气、暖通、通风、装饰、防水地坪等专业领域。'
              : 'Trình bày các dự án thành công của chúng tôi trên khắp Việt Nam, bao gồm các lĩnh vực chuyên môn như điện, HVAC, thông gió, trang trí, chống thấm sàn.'}
          </p>
        </div>
      </section>

      {/* 分类筛选 */}
      <section className="py-8 bg-white border-b">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap gap-2 md:gap-4 justify-center">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={cn(
                  'px-4 py-2 rounded-full transition-all duration-300',
                  selectedCategory === category.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                )}
              >
                {language === 'zh' ? category.nameZh : category.nameVi}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* 项目列表 */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-600">
                {language === 'zh' ? '加载项目中...' : 'Đang tải dự án...'}
              </p>
            </div>
          ) : error ? (
            <div className="text-center py-20">
              <div className="text-red-400 text-6xl mb-4">⚠️</div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">{error}</h3>
            </div>
          ) : (
            <>
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-2">
                  {language === 'zh' ? '精选项目' : 'Dự án nổi bật'}
                </h2>
                <p className="text-gray-600">
                  {selectedCategory === 'all'
                    ? (language === 'zh'
                        ? `共 ${projects.length} 个项目`
                        : `Tổng cộng ${projects.length} dự án`)
                    : (language === 'zh'
                        ? `${getCategoryName(selectedCategory)} - 共 ${projects.length} 个项目`
                        : `${getCategoryName(selectedCategory)} - Tổng cộng ${projects.length} dự án`)}
                </p>
              </div>

              {projects.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {projects.map((project) => (
                    <div
                      key={project.id}
                      onClick={() => handleProjectClick(project.id)}
                      className="block cursor-pointer"
                      role="link"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleProjectClick(project.id);
                        }
                      }}
                    >
                      <ProjectCard
                        project={project}
                        language={language}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="text-gray-400 text-6xl mb-4">📂</div>
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">
                    {language === 'zh' ? '暂无项目' : 'Chưa có dự án'}
                  </h3>
                  <p className="text-gray-500">
                    {language === 'zh'
                      ? '该分类下暂无项目案例，请选择其他分类查看。'
                      : 'Chưa có dự án nào trong danh mục này, vui lòng chọn danh mục khác.'}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* 项目统计 */}
      <section className="py-16 bg-blue-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">
              {language === 'zh' ? '项目成就统计' : 'Thống kê thành tựu dự án'}
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            <div className="text-center">
              <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-blue-600 mb-1 md:mb-2">150+</div>
              <div className="text-xs sm:text-sm md:text-base text-gray-700 font-medium">
                {language === 'zh' ? '完成项目' : 'Dự án hoàn thành'}
              </div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-blue-600 mb-2">98%</div>
              <div className="text-gray-700 font-medium">
                {language === 'zh' ? '客户满意度' : 'Hài lòng khách hàng'}
              </div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-blue-600 mb-2">25+</div>
              <div className="text-gray-700 font-medium">
                {language === 'zh' ? '服务城市' : 'Thành phố phục vụ'}
              </div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-blue-600 mb-2">50+</div>
              <div className="text-gray-700 font-medium">
                {language === 'zh' ? '合作客户' : 'Khách hàng hợp tác'}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 md:py-16 bg-gradient-to-r from-blue-700 to-blue-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4 md:mb-6">
            {language === 'zh' ? '开始您的下一个项目' : 'Bắt đầu dự án tiếp theo của bạn'}
          </h2>
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center items-center">
            <a
              href="/contact"
              className="w-full sm:w-auto px-6 md:px-8 py-3 bg-white text-blue-700 font-semibold rounded-lg hover:bg-gray-100 transition-colors text-sm md:text-base"
            >
              {language === 'zh' ? '联系我们' : 'Liên hệ ngay'}
            </a>
            <a
              href="/services"
              className="w-full sm:w-auto px-6 md:px-8 py-3 bg-transparent border-2 border-white text-white font-semibold rounded-lg hover:bg-white/10 transition-colors text-sm md:text-base"
            >
              {language === 'zh' ? '查看服务' : 'Xem dịch vụ'}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProjectsPage;
